import React from 'react';
import PropTypes from 'prop-types';
import { useSnackbarContext } from 'Context/SnackbarContext';

const UnlinkContext = React.createContext(undefined);

// Actions
export const UNLINK_START = 'unlink-started';
export const UNLINK_SUCCEEDED = 'unlink-succeeded';
export const UNLINK_FAILED = 'unlink-failed';

const init = () => {
  return {
    isUnlinking: false,
    unlinkingError: null,
    unlinkingResponse: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case UNLINK_START: {
      return {
        ...state,
        isUnlinking: true,
        unlinkingError: null,
      };
    }
    case UNLINK_SUCCEEDED: {
      return {
        ...state,
        isUnlinking: false,
        unlinkingError: null,
      };
    }
    case UNLINK_FAILED: {
      return {
        ...state,
        isUnlinking: false,
        unlinkingError: action.unlinkingError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const UnlinkProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const handleUnlink = React.useCallback(async (resource, onSuccess) => {
    try {
      dispatch({ type: UNLINK_START });
      const resp = await resource.delete();
      if (typeof onSuccess === 'function') {
        await onSuccess();
      }
      dispatch({ type: UNLINK_SUCCEEDED, unlinkingResponse: resp });
      displaySuccess('Successfully unlinked connection');
    } catch (err) {
      dispatch({ type: UNLINK_FAILED, unlinkingError: err });
      displayErrorMessage(err, 'Failed to unlink connection');
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isUnlinking: state.isUnlinking,
      unlinkingError: state.unlinkingError,
      unlinkingResponse: state.unlinkingResponse,
      handleUnlink,
    };
  }, [
    state,
    handleUnlink,
  ]);

  return (
    <UnlinkContext.Provider value={memoizedValue}>
      {children}
    </UnlinkContext.Provider>
  );
};

// Use Survey State Context
const useUnlinkContext = () => {
  const context = React.useContext(UnlinkContext);
  if (context === undefined) {
    throw new Error('useUnlinkContext must be used within a UnlinkProvider');
  }
  return context;
};

UnlinkProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  UnlinkProvider,
  useUnlinkContext,
};
